import { CheckCircleFilled, InboxOutlined } from "@ant-design/icons"
import {
  Alert,
  Button,
  Card,
  Flex,
  Layout,
  List,
  Progress,
  Result,
  Spin,
  Typography,
  Upload
} from "antd"
import dayjs from "dayjs"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useLocation } from "react-router-dom"

import {
  openUploadLink,
  uploadCustomerFile
} from "./api/customer-upload.services"
import type { OpenedUploadLink, UploadResponse, UploadResult } from "./types"

const { Title, Text } = Typography

type Stage = "opening" | "unavailable" | "full" | "ready" | "sending" | "sent"

interface QueuedFile {
  uid: string
  file: File
  status: "queued" | "uploading" | "done" | "error"
  percent: number
  // The server's own reason for refusing this file, when it gave one.
  detail?: string
}

const MB = 1024 * 1024

const extensionOf = (name: string) => {
  const dot = name.lastIndexOf(".")
  return dot > 0 ? name.slice(dot + 1).toLowerCase() : ""
}

// How the page names each allowed extension; one the page doesn't know is shown as itself.
const TYPE_GROUPS: Record<string, string> = {
  pdf: "pdf",
  doc: "word",
  docx: "word",
  xls: "excel",
  xlsx: "excel",
  ppt: "powerpoint",
  pptx: "powerpoint",
  txt: "text",
  csv: "text",
  jpg: "images",
  jpeg: "images",
  png: "images",
  heic: "images",
  heif: "images",
  webp: "images",
  gif: "images",
  mp4: "video",
  mov: "video",
  zip: "zip"
}

// The fragment is wiped once read; this tab-scoped copy is what lets a reload —
// or a mobile browser discarding the tab behind the camera/file picker — find
// the link again. Storage can throw (private mode, blocked site data).
const TOKEN_KEY = "ezbuild.customerUpload.token"
const storedToken = () => {
  try {
    return sessionStorage.getItem(TOKEN_KEY)
  } catch {
    return null
  }
}
const storeToken = (value: string | null) => {
  try {
    if (value) sessionStorage.setItem(TOKEN_KEY, value)
    else sessionStorage.removeItem(TOKEN_KEY)
  } catch {
    // Without storage the page still works; only a reload loses the link.
  }
}

/**
 * The page a customer reaches from their meeting invitation. No account, no app
 * shell. The token is read once from the fragment, then wiped from the address
 * bar — the same handling as the signing page — and kept in memory and this
 * tab's sessionStorage.
 */
const CustomerUploadPage: React.FC = () => {
  const { t } = useTranslation()
  const { hash } = useLocation()
  const opening = useRef<string | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [link, setLink] = useState<OpenedUploadLink | null>(null)
  const [stage, setStage] = useState<Stage>("opening")
  const [queue, setQueue] = useState<QueuedFile[]>([])
  const [problems, setProblems] = useState<string[]>([])

  useEffect(() => {
    const meta = document.createElement("meta")
    meta.name = "referrer"
    meta.content = "no-referrer"
    document.head.appendChild(meta)
    return () => {
      document.head.removeChild(meta)
    }
  }, [])

  // Only a 404 means the link is dead; a network blip or 5xx keeps the stored
  // token so a reload can try again.
  const unavailable = (status?: number) => {
    if (status === 404) storeToken(null)
    setStage("unavailable")
  }

  useEffect(() => {
    const fromHash = hash.replace(/^#/, "")
    const found = fromHash || storedToken()
    if (!found) {
      if (opening.current === null) setStage("unavailable")
      return
    }
    if (opening.current === found) return
    opening.current = found
    setToken(found)
    if (fromHash) {
      storeToken(fromHash)
      window.history.replaceState(null, "", window.location.pathname)
    }

    void openUploadLink(found)
      .then((response) => {
        if (!response.ok || !response.data) {
          unavailable(response.status)
          return
        }
        setLink(response.data)
        setStage(response.data.remaining > 0 ? "ready" : "full")
      })
      // The service already turns a 5xx/network failure into `ok: false`, but
      // a caller (or a test mocking the service directly) can still reject —
      // without this the spinner would spin forever.
      .catch(() => unavailable())
  }, [hash])

  const remaining = link?.remaining ?? 0
  const maxBytes = link?.max_file_size_bytes ?? 10 * MB
  const allowed = link?.allowed_extensions ?? []
  const isAllowed = (name: string) =>
    !allowed.length || allowed.includes(extensionOf(name))
  const allowedLabel = [
    ...new Set(
      allowed.map((ext) =>
        TYPE_GROUPS[ext]
          ? t(`customerUpload.typeGroups.${TYPE_GROUPS[ext]}`)
          : ext.toUpperCase()
      )
    )
  ].join(", ")
  const pending = queue.filter((q) => q.status !== "done")

  // antd calls beforeUpload once per file of a multi-file drop before React
  // re-renders, so the count of what is already queued lives in a ref; state
  // updaters stay pure (StrictMode runs them twice).
  const queueRef = useRef<QueuedFile[]>([])
  const updateQueue = (next: (current: QueuedFile[]) => QueuedFile[]) => {
    queueRef.current = next(queueRef.current)
    setQueue(queueRef.current)
  }
  const warn = (message: string) =>
    setProblems((current) =>
      current.includes(message) ? current : [...current, message]
    )

  const addFile = (file: File) => {
    const waiting = queueRef.current.filter((q) => q.status !== "done").length
    if (!isAllowed(file.name)) {
      warn(t("customerUpload.notAllowed", { name: file.name }))
    } else if (file.size > maxBytes) {
      warn(
        t("customerUpload.tooLarge", { name: file.name, size: maxBytes / MB })
      )
    } else if (file.size === 0) {
      warn(t("customerUpload.empty", { name: file.name }))
    } else if (waiting >= remaining) {
      warn(t("customerUpload.tooMany", { remaining }))
    } else {
      const uid = `${file.name}-${file.size}-${Date.now()}-${Math.random()}`
      updateQueue((current) => [
        ...current,
        { uid, file, status: "queued", percent: 0 }
      ])
    }
    return false
  }

  const patch = (uid: string, change: Partial<QueuedFile>) =>
    updateQueue((current) =>
      current.map((q) => (q.uid === uid ? { ...q, ...change } : q))
    )

  const sendAll = useCallback(
    async (items: QueuedFile[]) => {
      if (!token || !items.length) return
      setStage("sending")
      let left = remaining
      let failed = false
      let refusedAsFull = false
      // One at a time: a request stays under any body limit in front of the
      // API, and a failure costs the customer one file, not the batch.
      for (const item of items) {
        patch(item.uid, { status: "uploading", percent: 0, detail: undefined })
        // Belt and braces alongside the service's own catch: this costs one
        // file, not the whole queue getting stuck on "Sending…" forever.
        const response = await uploadCustomerFile(token, item.file, (percent) =>
          patch(item.uid, { percent })
        ).catch((): UploadResponse<UploadResult> => ({
          ok: false,
          status: 0,
          data: null,
          refusal: { reason: "unknown" }
        }))
        if (response.ok && response.data) {
          left = response.data.remaining
          patch(item.uid, { status: "done", percent: 100 })
        } else {
          failed = true
          if (response.status === 404) {
            unavailable(404)
            return
          }
          // e.g. slots used up from another device: the server's count wins.
          if (typeof response.refusal?.remaining === "number") {
            left = response.refusal.remaining
            refusedAsFull = left <= 0
          }
          patch(item.uid, { status: "error", detail: response.refusal?.detail })
        }
      }
      setLink((current) =>
        current ? { ...current, remaining: left } : current
      )
      setStage(refusedAsFull && left <= 0 ? "full" : failed ? "ready" : "sent")
    },
    [token, remaining]
  )

  const canSend =
    stage === "ready" && pending.some((q) => q.status === "queued")
  const sendQueued = () =>
    void sendAll(queue.filter((q) => q.status === "queued"))

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== "Enter") return
    const target = event.target as HTMLElement
    // rc-upload's own drag area is a focusable role="button" that opens the
    // file picker on Enter without stopping propagation, so an Enter that
    // originated there would otherwise also trigger this card-level send.
    if (target instanceof HTMLButtonElement || target.closest(".ant-upload"))
      return
    if (!canSend) return
    event.preventDefault()
    sendQueued()
  }

  const shell = (content: React.ReactNode) => (
    <Layout className={"min-h-screen bg-[#f8f9fa]"}>
      <Flex justify={"center"} className={"px-4 py-10"}>
        <Card
          data-testid={"customer-upload-card"}
          tabIndex={0}
          onKeyDown={onKeyDown}
          className={"w-full max-w-[560px]"}
        >
          {content}
        </Card>
      </Flex>
    </Layout>
  )

  if (stage === "opening")
    return shell(
      <Flex justify={"center"}>
        <Spin />
      </Flex>
    )
  if (stage === "unavailable")
    return shell(
      <Result
        status={"warning"}
        title={t("customerUpload.unavailableTitle")}
        subTitle={t("customerUpload.unavailableDetail")}
      />
    )
  if (stage === "full")
    return shell(
      <Result
        status={"info"}
        title={t("customerUpload.limitTitle")}
        subTitle={t("customerUpload.limitDetail")}
      />
    )
  if (stage === "sent")
    return shell(
      <Result
        status={"success"}
        title={t("customerUpload.successTitle")}
        subTitle={
          remaining > 0
            ? t("customerUpload.successRemaining", { remaining })
            : undefined
        }
        extra={
          remaining > 0 ? (
            <Button
              onClick={() => {
                updateQueue(() => [])
                setProblems([])
                setStage("ready")
              }}
            >
              {t("customerUpload.sendMore")}
            </Button>
          ) : undefined
        }
      />
    )

  return shell(
    <Flex vertical gap={16}>
      <div>
        <Title level={3} className={"!mb-1"}>
          {t("customerUpload.title")}
        </Title>
        <Text type={"secondary"}>
          {link?.company_name
            ? t("customerUpload.subtitle", { company: link.company_name })
            : t("customerUpload.subtitleNoCompany")}
        </Text>
        {link && (
          <Text className={"block"}>
            {t("customerUpload.meeting", {
              title: link.meeting_title,
              date: dayjs(link.meeting_start).format("MMM D, YYYY h:mm A")
            })}
          </Text>
        )}
      </div>

      {/* rc-upload silently drops a dragged file its `accept` rules out; this says so. */}
      <div
        onDropCapture={(event) =>
          Array.from(event.dataTransfer?.files ?? [])
            .filter((file) => !isAllowed(file.name))
            .forEach((file) =>
              warn(t("customerUpload.notAllowed", { name: file.name }))
            )
        }
      >
        <Upload.Dragger
          multiple
          showUploadList={false}
          beforeUpload={addFile}
          disabled={stage === "sending"}
          accept={
            allowed.length
              ? allowed.map((ext) => `.${ext}`).join(",")
              : undefined
          }
        >
          <p className={"ant-upload-drag-icon"}>
            <InboxOutlined />
          </p>
          <p>{t("customerUpload.dropHint")}</p>
          <Text type={"secondary"}>
            {t("customerUpload.limits", { remaining, size: maxBytes / MB })}
          </Text>
          {allowedLabel && (
            <Text type={"secondary"} className={"block"}>
              {t("customerUpload.allowed", { types: allowedLabel })}
            </Text>
          )}
        </Upload.Dragger>
      </div>

      {problems.map((problem) => (
        <Alert
          key={problem}
          type={"warning"}
          showIcon
          message={problem}
          closable
          onClose={() => setProblems((p) => p.filter((x) => x !== problem))}
        />
      ))}

      {queue.length > 0 && (
        <List
          dataSource={queue}
          renderItem={(item) => (
            <List.Item
              actions={[
                (item.status === "queued" || item.status === "error") && (
                  <Button
                    key={"remove"}
                    type={"link"}
                    size={"small"}
                    onClick={() =>
                      updateQueue((current) =>
                        current.filter((x) => x.uid !== item.uid)
                      )
                    }
                  >
                    {t("customerUpload.remove")}
                  </Button>
                ),
                item.status === "error" && (
                  <Button
                    key={"retry"}
                    type={"link"}
                    size={"small"}
                    onClick={() => void sendAll([item])}
                  >
                    {t("customerUpload.retry")}
                  </Button>
                ),
                item.status === "done" && (
                  <CheckCircleFilled
                    key={"done"}
                    className={"text-green-600"}
                    aria-label={t("customerUpload.done")}
                  />
                )
              ].filter(Boolean)}
            >
              <Flex vertical className={"min-w-0 flex-1"}>
                <Text ellipsis>{item.file.name}</Text>
                {item.status === "uploading" && (
                  <Progress percent={item.percent} size={"small"} />
                )}
                {item.status === "error" && (
                  <Text type={"danger"}>
                    {item.detail || t("customerUpload.failed")}
                  </Text>
                )}
              </Flex>
            </List.Item>
          )}
        />
      )}

      <Button
        type={"primary"}
        size={"large"}
        block
        disabled={!canSend}
        loading={stage === "sending"}
        onClick={sendQueued}
      >
        {stage === "sending"
          ? t("customerUpload.sending")
          : t("customerUpload.send")}
      </Button>
    </Flex>
  )
}

export default CustomerUploadPage
